import { NavLink } from "react-router-dom";

import { DataTableCard } from "../components/DataTableCard";
import { MetricCard } from "../components/MetricCard";
import { NumberedListCard, type NumberedListItem } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { SignalStrip } from "../components/SignalStrip";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type MarketSupportPageData = Awaited<ReturnType<typeof loadMarketSupportPage>>;

async function loadMarketSupportPage() {
  const [demand, comps, precedent, concept] = await Promise.all([
    api.getDemand(),
    api.getComps(),
    api.getPrecedent(),
    api.getConcept()
  ]);

  return { demand, comps, precedent, concept };
}

function uniqueItems(items: string[]) {
  const seen = new Set<string>();

  return items
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) {
        return false;
      }

      const key = item.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function toSentence(text: string) {
  const trimmed = text.trim();
  const firstSentence = trimmed.match(/.*?[.?!](?:\s|$)/);
  return firstSentence ? firstSentence[0].trim() : trimmed;
}

function buildCompRows(data: MarketSupportPageData["comps"]["compsTable"]) {
  return data.referenceSet.slice(0, 5).map((item) => [
    item.compName,
    item.productType,
    item.notableSignal,
    item.relevance
  ]);
}

function buildPrecedentRows(data: MarketSupportPageData["precedent"]) {
  return data.referenceProperties.slice(0, 4).map((item) => [
    item.address,
    item.saleSignal,
    `${item.size} · ${item.bedroomsBaths}`,
    item.keyImplication
  ]);
}

function buildStrengthRows(data: MarketSupportPageData["demand"]) {
  return data.strengthAssessment.slice(0, 4).map((item) => [item.category, item.currentRead, item.notes]);
}

function FooterLinkCard({
  eyebrow,
  title,
  body,
  href
}: {
  eyebrow: string;
  title: string;
  body: string;
  href: string;
}) {
  return (
    <NavLink
      to={href}
      style={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        borderRadius: "22px",
        padding: "22px",
        background: "linear-gradient(180deg, rgba(11, 17, 30, 0.92), rgba(8, 12, 22, 0.8))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 18px 40px rgba(3, 8, 20, 0.18)"
      }}
    >
      <div className="stack-sm">
        <span className="card-eyebrow">{eyebrow}</span>
        <h3 className="card-title">{title}</h3>
        <p className="card-copy">{body}</p>
      </div>
    </NavLink>
  );
}

export function MarketSupportPage() {
  const { data, loading, error } = useContent("market-support-page", loadMarketSupportPage);

  if (!data) {
    return (
      <PageContainer
        eyebrow="Market Support"
        title="Commercial proof behind the lead concept."
        lede="This page should clear one question quickly: does the local market support the selected concept strongly enough to matter?"
      >
        {loading ? <div className="loading-panel shimmer-card">Loading market support...</div> : null}
        {error ? <div className="error-panel">{error}</div> : null}
      </PageContainer>
    );
  }

  const { demand, comps, precedent, concept } = data;
  const leadConcept = concept.conceptSummary.leadConcept;
  const referenceCompCount = comps.compsTable.referenceSet.length;
  const boundaryItems = uniqueItems([
    ...demand.risks,
    ...comps.compsSummary.limits,
    ...precedent.limits,
    ...concept.conceptSummary.assumptions
  ]);
  const boundaryCount = boundaryItems.length;

  const demandItems: NumberedListItem[] = demand.demandDrivers.slice(0, 4).map((item) => ({
    label: item.demandDriver,
    detail: item.whyItMatters
  }));
  const compSupportItems: NumberedListItem[] = comps.compsSummary.supports.slice(0, 4).map((item) => ({
    label: item.topic,
    detail: `${item.conclusion} (${item.confidence})`
  }));
  const precedentItems: NumberedListItem[] = precedent.takeaways.slice(0, 4).map((item) => ({
    label: item
  }));
  const conceptFitItems: NumberedListItem[] = [
    {
      label: leadConcept.corePositioning,
      detail: leadConcept.bestFitGuestThesis
    },
    {
      label: leadConcept.whyItLeads,
      detail: leadConcept.currentCompSupport
    },
    {
      label: toSentence(demand.currentDemandRead),
      detail: `Comparable read: ${toSentence(comps.compsSummary.currentCompRead)}`
    },
    {
      label: toSentence(precedent.currentInterpretation),
      detail: precedent.referenceProperties[0]?.keyImplication ?? "Hyper-local precedent remains visible."
    }
  ];
  const conceptProofRows = [
    [
      "Demand",
      demand.currentDemandRead,
      demand.demandDrivers[0]?.whyItMatters ?? "Local stay drivers remain visible."
    ],
    [
      "Comparable support",
      comps.compsSummary.currentCompRead,
      comps.compsSummary.supports[0]?.conclusion ?? "Comparable product realism is present."
    ],
    [
      "Precedent",
      precedent.currentInterpretation,
      precedent.referenceProperties[0]?.keyImplication ?? "Nearby built-product evidence stays usable."
    ],
    [
      "Lead concept",
      concept.conceptSummary.currentRecommendationRead,
      leadConcept.currentCompSupport
    ]
  ];
  const boundaryListItems: NumberedListItem[] = boundaryItems.slice(0, 5).map((item) => ({ label: item }));
  const guestSegmentsRead = demand.guestSegments.slice(0, 3).join(" · ");

  return (
    <PageContainer
      eyebrow="Market Support"
      title="Commercial proof behind the lead concept."
      lede="This page now leads with the commercial answer: whether the selected concept clears a bounded local market test, why it does, and where the evidence still stops."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading market support...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}

      <div className="stack-xl">
        <PremiumHero
          eyebrow="Commercial Read"
          title="Demand is visible. Comparable support is usable. The concept clears a bounded market test."
          subtitle="The current market case does not depend on an invented TAM figure. It rests on visible local demand drivers, usable comparable-product evidence, and hyper-local precedent that keeps the lead concept commercially plausible while a finite set of limits stays in view."
          backgroundImage="/comps/110-f-st-streetview.jpg"
          backgroundLabel="Grounded nearby built-product context"
          badges={
            <>
              <SignalPill label={comps.compsSummary.status} tone="positive" />
              <SignalPill label={leadConcept.concept} tone="neutral" />
              <SignalPill label={`${boundaryCount} live limits`} tone="warning" />
            </>
          }
          actions={
            <>
              <NavLink to="/investment-case" className="link-button link-button-primary">
                Return to Investment Case
              </NavLink>
              <NavLink to="/financials" className="link-button link-button-secondary">
                Open Financials
              </NavLink>
            </>
          }
          aside={
            <div className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">At a Glance</span>
                <div style={{ fontSize: "1.7rem", lineHeight: 1.05, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                  {leadConcept.concept}
                </div>
                <p style={{ margin: 0, color: "rgba(223, 233, 248, 0.78)", lineHeight: 1.5 }}>
                  {leadConcept.currentCompSupport}
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px"
                }}
              >
                {[
                  ["Market read", demand.currentDemandRead],
                  ["Reference comps", `${referenceCompCount}`],
                  ["Local precedents", `${precedent.referenceProperties.length}`],
                  ["Open boundaries", `${boundaryCount}`]
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "18px",
                      padding: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)"
                    }}
                  >
                    <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                      {label}
                    </div>
                    <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  overflow: "hidden",
                  borderRadius: "18px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.04)"
                }}
              >
                <img src="/comps/110-f-st-map.jpg" alt="Local market support map context" style={{ display: "block", width: "100%", height: "160px", objectFit: "cover" }} />
              </div>
            </div>
          }
        />

        <SignalStrip
          items={[
            {
              label: "Demand read",
              value: demand.currentDemandRead,
              note: demand.demandDrivers[0]?.demandDriver ?? demand.locationOverview,
              tone: "positive"
            },
            {
              label: "Comparable support",
              value: comps.compsSummary.currentCompRead,
              note: comps.compsSummary.supports[0]?.conclusion ?? comps.compsSummary.overview,
              tone: "positive"
            },
            {
              label: "Precedent read",
              value: precedent.currentInterpretation,
              note: precedent.referenceProperties[0]?.keyImplication ?? precedent.overview,
              tone: "neutral"
            },
            {
              label: "Lead concept",
              value: leadConcept.concept,
              note: leadConcept.bestFitGuestThesis,
              tone: "neutral"
            },
            {
              label: "Commercial limits",
              value: `${boundaryCount}`,
              note: boundaryItems[0] ?? "Visible constraints remain bounded.",
              tone: "warning"
            }
          ]}
        />

        <section className="stack-lg">
          <div className="stack-xs">
            <span className="card-eyebrow">Market Opportunity</span>
            <h2 className="card-title">A bounded local opportunity frame, not a synthetic market-size claim.</h2>
            <p className="card-copy" style={{ maxWidth: "62ch" }}>
              Exact TAM-style market sizing is not surfaced in the governed public package. The usable investor read is the depth of local demand support, the guest mix it points to, the comp evidence base, and the nearby precedent count that underwrites commercial plausibility.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "16px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
            }}
          >
            <MetricCard title="Demand drivers" value={`${demand.demandDrivers.length}`} detail="Current local demand pillars surfaced in the governed page payload" />
            <MetricCard title="Guest segments" value={`${demand.guestSegments.length}`} detail={guestSegmentsRead || "Current target demand groups"} />
            <MetricCard title="Reference comps" value={`${referenceCompCount}`} detail="Comparable product set currently carried into the public proof layer" />
            <MetricCard title="Local precedents" value={`${precedent.referenceProperties.length}`} detail="Nearby precedent properties currently surfaced" />
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
            }}
          >
            <NumberedListCard
              eyebrow="Demand Drivers"
              title="What brings guests to this pocket"
              description={demand.locationOverview}
              items={demandItems}
              tone="positive"
            />
            <DataTableCard
              eyebrow="Opportunity Frame"
              title="Current demand signals, not a synthetic TAM"
              description="The strongest currently grounded market-opportunity framing is the depth and consistency of the local demand read."
              columns={["Signal", "Current read", "Why it matters"]}
              rows={buildStrengthRows(demand)}
              footer={
                <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.9rem", lineHeight: 1.55 }}>
                  Guest segments currently surfaced: {guestSegmentsRead || "Guest segmentation remains partially bounded in the current public package."}
                </div>
              }
            />
          </div>
        </section>

        <section className="stack-lg">
          <div className="stack-xs">
            <span className="card-eyebrow">Comparable Support</span>
            <h2 className="card-title">Comparable product evidence now does the work directly.</h2>
            <p className="card-copy" style={{ maxWidth: "62ch" }}>
              The commercial case is stronger when the best comparable signals are visible in rows rather than buried in narrative. This section keeps the product analogs, pricing/positioning cues, and concept relevance explicit.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
            }}
          >
            <DataTableCard
              eyebrow="Comp Set"
              title="Strongest grounded comparable rows"
              description={comps.compsTable.earlyInterpretation}
              columns={["Comp", "Product", "Commercial signal", "Why it matters"]}
              rows={buildCompRows(comps.compsTable)}
            />
            <NumberedListCard
              eyebrow="Comp Support"
              title="What the comp set currently proves"
              description={comps.compsSummary.overview}
              items={compSupportItems}
              tone="neutral"
            />
          </div>
        </section>

        <section className="stack-lg">
          <div className="stack-xs">
            <span className="card-eyebrow">Precedent Support</span>
            <h2 className="card-title">Hyper-local precedent keeps the commercial case grounded.</h2>
            <p className="card-copy" style={{ maxWidth: "62ch" }}>
              Nearby precedent matters because it anchors the case in real local product, not in abstract market commentary. The strongest references are finite and visible here.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
            }}
          >
            <DataTableCard
              eyebrow="Local Precedents"
              title="Nearby precedent evidence"
              description={precedent.overview}
              columns={["Address", "Sale signal", "Property read", "Why it matters"]}
              rows={buildPrecedentRows(precedent)}
            />
            <NumberedListCard
              eyebrow="Precedent Takeaways"
              title="Why the nearby references matter"
              description={precedent.conclusion}
              items={precedentItems}
              tone="warning"
            />
          </div>
        </section>

        <section
          style={{
            borderRadius: "28px",
            padding: "24px",
            background: "linear-gradient(180deg, rgba(8, 13, 24, 0.94), rgba(7, 11, 20, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 50px rgba(3, 8, 20, 0.22)"
          }}
        >
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Why This Concept Clears</span>
              <h2 className="card-title">The selected concept is the one the current market evidence can actually carry.</h2>
              <p className="card-copy" style={{ maxWidth: "62ch" }}>
                This is not a generic options page. The selected concept leads because the demand read, comparable evidence, and local precedent align behind it more cleanly than they do behind the alternatives.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gap: "18px",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
              }}
            >
              <NumberedListCard
                eyebrow="Concept-Market Fit"
                title={leadConcept.concept}
                description={concept.conceptSummary.executiveRecommendation}
                items={conceptFitItems}
                tone="positive"
              />
              <DataTableCard
                eyebrow="Proof Stack"
                title="Why the market test clears today"
                description="The lead concept stays commercially plausible because the main proof layers point in the same direction."
                columns={["Proof layer", "Current read", "What it proves"]}
                rows={conceptProofRows}
              />
            </div>
          </div>
        </section>

        <section
          style={{
            borderRadius: "28px",
            padding: "24px",
            background: "linear-gradient(180deg, rgba(8, 13, 24, 0.94), rgba(7, 11, 20, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 50px rgba(3, 8, 20, 0.22)"
          }}
        >
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Commercial Boundaries</span>
              <h2 className="card-title">What the evidence still does not prove.</h2>
              <p className="card-copy" style={{ maxWidth: "62ch" }}>
                The commercial case is stronger after this rebuild, but it is still bounded. These are the live limitations that remain inside the market read without turning the page into a risk memo.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gap: "18px",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
              }}
            >
              <NumberedListCard
                eyebrow="Visible Limits"
                title="Current commercial boundaries"
                description="These items stay visible so the proof layer remains honest."
                items={boundaryListItems}
                tone="warning"
              />
              <div className="stack-sm">
                <FooterLinkCard
                  eyebrow="Return path"
                  title="Investment Case"
                  body="Go back to the high-conviction thesis now that the commercial proof layer is clearer."
                  href="/investment-case"
                />
                <FooterLinkCard
                  eyebrow="Next read"
                  title="Financials"
                  body="Pressure-test the selected concept against the current budget basis, underwriting, fallback, and disclosure posture."
                  href="/financials"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
